import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Plus, Edit2, Trash2, Eye, ExternalLink, Sparkles, Upload, Image, Video, 
  Package, X, GripVertical, ChefHat, FileText, Clock, Users, Tag, Percent, Megaphone,
  ListChecks, Check, Square, CheckSquare
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import RichTextEditor from '../RichTextEditor';
import { buildPreview } from '../../utils/stripHtml';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ContentManager = ({ headers, token }) => {
  const [activeTab, setActiveTab] = useState('blogs');
  const [blogs, setBlogs] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [showBlogDialog, setShowBlogDialog] = useState(false);
  const [showRecipeDialog, setShowRecipeDialog] = useState(false);
  const [showAIPrompt, setShowAIPrompt] = useState(false);
  const [aiCustomPrompt, setAiCustomPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingBlogId, setEditingBlogId] = useState(null);
  const [editingRecipeId, setEditingRecipeId] = useState(null);
  
  // Category Management State
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', slug: '', description: '' });
  
  // Bulk Assign Posts to Category State
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);
  const [bulkAssignCategoryId, setBulkAssignCategoryId] = useState(null);
  const [bulkAssignCategoryName, setBulkAssignCategoryName] = useState('');
  const [selectedPostIds, setSelectedPostIds] = useState([]);
  const [savingBulkAssign, setSavingBulkAssign] = useState(false);

  const [blogForm, setBlogForm] = useState({
    title: '', slug: '', content: '', excerpt: '', featured_image: '',
    category_id: '', tags: '', published: false, featured: false,
    video_url: '', featured_product_id: '', media: [],
    embedded_banner_ids: [], embedded_coupon_ids: [],
    banner_positions: {}, coupon_positions: {}
  });

  const [recipeForm, setRecipeForm] = useState({
    title: '', slug: '', description: '', featured_image: '',
    prep_time: '', rehydration_time: '', cook_time: '', servings: '',
    difficulty: '', categories: '', tags: '', published: false, featured: false,
    ingredients: '', instructions: '', video_url: '', featured_product_id: '', media: []
  });

  // AI Content Generation states
  const [aiOptions, setAiOptions] = useState({
    tone: 'informative',
    wordCount: 800,
    includeProducts: true,
    generateSlug: true,
    generateExcerpt: true,
    generateTags: true,
    generateMeta: true
  });

  // Marketing data for blog embeds
  const [banners, setBanners] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [uploadingFeaturedImage, setUploadingFeaturedImage] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [blogsRes, recipesRes, categoriesRes, productsRes, bannersRes, couponsRes] = await Promise.all([
        axios.get(`${API_URL}/api/blog/posts?published_only=false`, { headers }),
        axios.get(`${API_URL}/api/recipes?published_only=false`, { headers }),
        axios.get(`${API_URL}/api/blog/categories`),
        axios.get(`${API_URL}/api/products`),
        axios.get(`${API_URL}/api/marketing/banners`, { headers }),
        axios.get(`${API_URL}/api/marketing/coupons`, { headers })
      ]);
      setBlogs(blogsRes.data.posts || blogsRes.data || []);
      setRecipes(recipesRes.data.recipes || recipesRes.data || []);
      setCategories(categoriesRes.data || []);
      setProducts(productsRes.data || []);
      setBanners(bannersRes.data || []);
      setCoupons(couponsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch content data');
    }
  };

  // Featured image upload handler
  const handleFeaturedImageUpload = async (e, formType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingFeaturedImage(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', formType === 'blog' ? 'blogs' : 'recipes');

    try {
      const res = await axios.post(`${API_URL}/api/landing-pages/upload-media`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      
      // API returns file_url, convert to full URL with API base
      const imageUrl = res.data.file_url || res.data.url;
      if (imageUrl) {
        // Construct full URL for the image
        const fullUrl = imageUrl.startsWith('http') ? imageUrl : `${API_URL}/api${imageUrl}`;
        if (formType === 'blog') {
          setBlogForm(prev => ({ ...prev, featured_image: fullUrl }));
        } else {
          setRecipeForm(prev => ({ ...prev, featured_image: fullUrl }));
        }
        toast.success('Featured image uploaded');
      }
    } catch (err) {
      console.error('Featured image upload error:', err);
      toast.error('Failed to upload image');
    } finally {
      setUploadingFeaturedImage(false);
      e.target.value = '';
    }
  };

  // AI Content Generation
  const generateBlogContent = async () => {
    console.log('generateBlogContent called, title:', blogForm.title);
    if (!blogForm.title) {
      toast.error('Please enter a title first');
      return;
    }
    setAiGenerating(true);
    toast.info('AI is generating content... This may take 30-60 seconds.', { duration: 5000 });
    const customPrompt = getAiPromptValue();
    try {
      console.log('Making API call to /api/ai/blog');
      const res = await axios.post(`${API_URL}/api/ai/blog`, {
        topic: blogForm.title,
        custom_prompt: customPrompt || undefined,
        tone: aiOptions.tone,
        target_word_count: aiOptions.wordCount,
        include_product_mentions: aiOptions.includeProducts
      }, { headers, timeout: 120000 }); // 2 minute timeout
      
      console.log('AI Response:', res.data);
      const generated = res.data.content || res.data;
      
      console.log('Generated object:', generated);
      console.log('Generated content HTML:', generated.content?.substring(0, 200));
      
      // Update form with generated content
      const newContent = generated.content || generated.body_html || '';
      const updates = {
        content: newContent
      };
      
      if (aiOptions.generateSlug && generated.slug) {
        updates.slug = generated.slug;
      }
      if (aiOptions.generateExcerpt && generated.excerpt) {
        updates.excerpt = generated.excerpt;
      }
      if (aiOptions.generateTags && generated.tags) {
        updates.tags = Array.isArray(generated.tags) ? generated.tags.join(', ') : generated.tags;
      }
      if (aiOptions.generateMeta) {
        if (generated.meta_title) updates.meta_title = generated.meta_title;
        if (generated.meta_description) updates.meta_description = generated.meta_description;
      }
      
      console.log('Updating blog form with:', Object.keys(updates), 'Content length:', newContent?.length);
      
      // Force state update by creating new object
      setBlogForm(prev => {
        const newForm = { ...prev, ...updates };
        console.log('New form state content length:', newForm.content?.length);
        return newForm;
      });
      
      toast.success('Content generated successfully!');
      setShowAIPrompt(false);
      setAiCustomPrompt('');
    } catch (error) {
      console.error('AI generation error:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('Generation timed out. Please try again or use a shorter word count.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to generate content. Please try again.');
      }
    } finally {
      setAiGenerating(false);
    }
  };

  const generateRecipeContent = async () => {
    if (!recipeForm.title) {
      toast.error('Please enter a recipe name first');
      return;
    }
    setAiGenerating(true);
    toast.info('AI is generating recipe... This may take 30-60 seconds.', { duration: 5000 });
    const customPrompt = getAiPromptValue();
    try {
      const res = await axios.post(`${API_URL}/api/ai/recipe`, {
        recipe_name: recipeForm.title,
        custom_prompt: customPrompt || undefined,
        food_type: 'general',
        difficulty: recipeForm.difficulty || 'medium',
        style: 'detailed'
      }, { headers, timeout: 120000 }); // 2 minute timeout
      
      console.log('Recipe AI Response:', res.data);
      const generated = res.data.content || res.data;
      
      setRecipeForm(prev => ({
        ...prev,
        description: generated.description || prev.description,
        ingredients: generated.ingredients_html || generated.ingredients || prev.ingredients,
        instructions: generated.instructions_html || generated.instructions || prev.instructions,
        prep_time: generated.prep_time?.toString() || prev.prep_time,
        cook_time: generated.cook_time?.toString() || prev.cook_time,
        rehydration_time: generated.rehydration_time?.toString() || prev.rehydration_time,
        servings: generated.servings?.toString() || prev.servings,
        difficulty: generated.difficulty || prev.difficulty,
        tags: Array.isArray(generated.tags) ? generated.tags.join(', ') : (generated.tags || prev.tags),
        slug: generated.slug || prev.slug
      }));
      toast.success('Recipe content generated successfully!');
      setShowAIPrompt(false);
      setAiCustomPrompt('');
    } catch (error) {
      console.error('Recipe AI generation error:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('Generation timed out. Please try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to generate recipe content. Please try again.');
      }
    } finally {
      setAiGenerating(false);
    }
  };

  // Media Upload
  const handleMediaUpload = async (e, type, formSetter) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const uploadedMedia = [];
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('content_type', type);
      
      try {
        const res = await axios.post(`${API_URL}/api/content/upload-media`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
        });
        uploadedMedia.push({
          type: file.type.startsWith('video/') ? 'video' : 'image',
          url: res.data.url,
          filename: file.name
        });
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    if (uploadedMedia.length > 0) {
      formSetter(prev => ({
        ...prev,
        media: [...(prev.media || []), ...uploadedMedia]
      }));
      toast.success(`${uploadedMedia.length} file(s) uploaded`);
    }
    setUploading(false);
  };

  const removeMedia = (index, formSetter) => {
    formSetter(prev => ({
      ...prev,
      media: prev.media.filter((_, i) => i !== index)
    }));
  };

  // Blog Handlers
  const handleSaveBlog = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...blogForm,
        tags: blogForm.tags ? blogForm.tags.split(',').map(t => t.trim()) : []
      };
      
      if (editingBlogId) {
        await axios.put(`${API_URL}/api/blog/posts/${editingBlogId}`, data, { headers });
        toast.success('Blog post updated!');
      } else {
        await axios.post(`${API_URL}/api/blog/posts`, data, { headers });
        toast.success('Blog post created!');
      }
      
      resetBlogForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save blog post');
    }
  };

  const handleEditBlog = (post) => {
    setBlogForm({
      title: post.title,
      slug: post.slug,
      content: post.content,
      excerpt: post.excerpt || '',
      featured_image: post.featured_image || '',
      category_id: post.category_id || '',
      tags: post.tags?.join(', ') || '',
      published: post.published,
      featured: post.featured,
      video_url: post.video_url || '',
      featured_product_id: post.featured_product_id || '',
      media: post.media || [],
      embedded_banner_ids: post.embedded_banner_ids || [],
      embedded_coupon_ids: post.embedded_coupon_ids || [],
      banner_positions: post.banner_positions || {},
      coupon_positions: post.coupon_positions || {}
    });
    setEditingBlogId(post.id);
    setShowBlogDialog(true);
  };

  const handleDeleteBlog = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await axios.delete(`${API_URL}/api/blog/posts/${id}`, { headers });
      toast.success('Blog post deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete blog post');
    }
  };

  const resetBlogForm = () => {
    setShowBlogDialog(false);
    setEditingBlogId(null);
    setBlogForm({
      title: '', slug: '', content: '', excerpt: '', featured_image: '',
      category_id: '', tags: '', published: false, featured: false,
      video_url: '', featured_product_id: '', media: [],
      embedded_banner_ids: [], embedded_coupon_ids: [],
      banner_positions: {}, coupon_positions: {}
    });
  };

  // Banner and Coupon toggle functions for blog
  const toggleBannerInBlog = (bannerId) => {
    setBlogForm(prev => {
      const isSelected = prev.embedded_banner_ids.includes(bannerId);
      return {
        ...prev,
        embedded_banner_ids: isSelected
          ? prev.embedded_banner_ids.filter(id => id !== bannerId)
          : [...prev.embedded_banner_ids, bannerId],
        banner_positions: isSelected
          ? Object.fromEntries(Object.entries(prev.banner_positions).filter(([k]) => k !== bannerId))
          : { ...prev.banner_positions, [bannerId]: 'middle' }
      };
    });
  };

  const toggleCouponInBlog = (couponId) => {
    setBlogForm(prev => {
      const isSelected = prev.embedded_coupon_ids.includes(couponId);
      return {
        ...prev,
        embedded_coupon_ids: isSelected
          ? prev.embedded_coupon_ids.filter(id => id !== couponId)
          : [...prev.embedded_coupon_ids, couponId],
        coupon_positions: isSelected
          ? Object.fromEntries(Object.entries(prev.coupon_positions).filter(([k]) => k !== couponId))
          : { ...prev.coupon_positions, [couponId]: 'bottom' }
      };
    });
  };

  const updateBannerPosition = (bannerId, position) => {
    setBlogForm(prev => ({
      ...prev,
      banner_positions: { ...prev.banner_positions, [bannerId]: position }
    }));
  };

  const updateCouponPosition = (couponId, position) => {
    setBlogForm(prev => ({
      ...prev,
      coupon_positions: { ...prev.coupon_positions, [couponId]: position }
    }));
  };

  // Recipe Handlers
  const handleSaveRecipe = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...recipeForm,
        prep_time: recipeForm.prep_time ? parseInt(recipeForm.prep_time) : null,
        rehydration_time: recipeForm.rehydration_time ? parseInt(recipeForm.rehydration_time) : null,
        cook_time: recipeForm.cook_time ? parseInt(recipeForm.cook_time) : null,
        servings: recipeForm.servings ? parseInt(recipeForm.servings) : null,
        categories: recipeForm.categories ? recipeForm.categories.split(',').map(c => c.trim()) : [],
        tags: recipeForm.tags ? recipeForm.tags.split(',').map(t => t.trim()) : [],
        ingredients_html: recipeForm.ingredients,
        instructions_html: recipeForm.instructions
      };
      
      if (editingRecipeId) {
        await axios.put(`${API_URL}/api/recipes/${editingRecipeId}`, data, { headers });
        toast.success('Recipe updated!');
      } else {
        await axios.post(`${API_URL}/api/recipes`, data, { headers });
        toast.success('Recipe created!');
      }
      
      resetRecipeForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save recipe');
    }
  };

  const handleEditRecipe = (recipe) => {
    setRecipeForm({
      title: recipe.title,
      slug: recipe.slug,
      description: recipe.description || '',
      featured_image: recipe.featured_image || '',
      prep_time: recipe.prep_time?.toString() || '',
      rehydration_time: recipe.rehydration_time?.toString() || '',
      cook_time: recipe.cook_time?.toString() || '',
      servings: recipe.servings?.toString() || '',
      difficulty: recipe.difficulty || '',
      categories: recipe.categories?.join(', ') || '',
      tags: recipe.tags?.join(', ') || '',
      published: recipe.published,
      featured: recipe.featured,
      ingredients: recipe.ingredients_html || recipe.ingredients || '',
      instructions: recipe.instructions_html || recipe.instructions || '',
      video_url: recipe.video_url || '',
      featured_product_id: recipe.featured_product_id || '',
      media: recipe.media || []
    });
    setEditingRecipeId(recipe.id);
    setShowRecipeDialog(true);
  };

  const handleDeleteRecipe = async (id) => {
    if (!window.confirm('Delete this recipe?')) return;
    try {
      await axios.delete(`${API_URL}/api/recipes/${id}`, { headers });
      toast.success('Recipe deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete recipe');
    }
  };

  const resetRecipeForm = () => {
    setShowRecipeDialog(false);
    setEditingRecipeId(null);
    setRecipeForm({
      title: '', slug: '', description: '', featured_image: '',
      prep_time: '', rehydration_time: '', cook_time: '', servings: '',
      difficulty: '', categories: '', tags: '', published: false, featured: false,
      ingredients: '', instructions: '', video_url: '', featured_product_id: '', media: []
    });
  };

  // ============== CATEGORY MANAGEMENT ==============
  const handleSaveCategory = async (e) => {
    e.preventDefault();
    try {
      const slug = categoryForm.slug || categoryForm.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      const data = { ...categoryForm, slug };
      
      if (editingCategoryId) {
        await axios.put(`${API_URL}/api/blog/categories/${editingCategoryId}`, data, { headers });
        toast.success('Category updated!');
      } else {
        await axios.post(`${API_URL}/api/blog/categories`, data, { headers });
        toast.success('Category created!');
      }
      
      resetCategoryForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleEditCategory = (cat) => {
    setCategoryForm({ name: cat.name, slug: cat.slug, description: cat.description || '' });
    setEditingCategoryId(cat.id);
    setShowCategoryDialog(true);
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Delete this category? Blog posts using this category will be unassigned.')) return;
    try {
      await axios.delete(`${API_URL}/api/blog/categories/${id}`, { headers });
      toast.success('Category deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const resetCategoryForm = () => {
    setShowCategoryDialog(false);
    setEditingCategoryId(null);
    setCategoryForm({ name: '', slug: '', description: '' });
  };

  // ============== BULK ASSIGN POSTS TO CATEGORY ==============
  const handleOpenBulkAssign = (category) => {
    setBulkAssignCategoryId(category.id);
    setBulkAssignCategoryName(category.name);
    // Pre-select posts that already belong to this category
    const postsInCategory = blogs.filter(b => b.category_id === category.id).map(b => b.id);
    setSelectedPostIds(postsInCategory);
    setShowBulkAssignDialog(true);
  };

  const handleTogglePostSelection = (postId) => {
    setSelectedPostIds(prev => 
      prev.includes(postId) 
        ? prev.filter(id => id !== postId)
        : [...prev, postId]
    );
  };

  const handleSelectAllPosts = () => {
    if (selectedPostIds.length === blogs.length) {
      setSelectedPostIds([]);
    } else {
      setSelectedPostIds(blogs.map(b => b.id));
    }
  };

  const handleSaveBulkAssign = async () => {
    setSavingBulkAssign(true);
    try {
      // Find posts to add to this category (selected but not currently in category)
      // Find posts to remove from this category (in category but not selected)
      const currentPostsInCategory = blogs.filter(b => b.category_id === bulkAssignCategoryId).map(b => b.id);
      
      const postsToAdd = selectedPostIds.filter(id => !currentPostsInCategory.includes(id));
      const postsToRemove = currentPostsInCategory.filter(id => !selectedPostIds.includes(id));
      
      // Update posts that need to be added to category
      const addPromises = postsToAdd.map(postId => 
        axios.put(`${API_URL}/api/blog/posts/${postId}`, { category_id: bulkAssignCategoryId }, { headers })
      );
      
      // Update posts that need to be removed from category (set to null/empty)
      const removePromises = postsToRemove.map(postId =>
        axios.put(`${API_URL}/api/blog/posts/${postId}`, { category_id: '' }, { headers })
      );
      
      await Promise.all([...addPromises, ...removePromises]);
      
      const totalChanges = postsToAdd.length + postsToRemove.length;
      if (totalChanges > 0) {
        toast.success(`Updated ${totalChanges} post(s) for "${bulkAssignCategoryName}"`);
      } else {
        toast.info('No changes made');
      }
      
      resetBulkAssignDialog();
      fetchData();
    } catch (error) {
      toast.error('Failed to update posts');
      console.error(error);
    } finally {
      setSavingBulkAssign(false);
    }
  };

  const resetBulkAssignDialog = () => {
    setShowBulkAssignDialog(false);
    setBulkAssignCategoryId(null);
    setBulkAssignCategoryName('');
    setSelectedPostIds([]);
  };

  // ============== QUICK CATEGORY CHANGE ==============
  const handleQuickCategoryChange = async (postId, newCategoryId) => {
    try {
      await axios.put(`${API_URL}/api/blog/posts/${postId}`, { category_id: newCategoryId || '' }, { headers });
      const categoryName = newCategoryId ? categories.find(c => c.id === newCategoryId)?.name : 'None';
      toast.success(`Category updated to "${categoryName || 'None'}"`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update category');
      console.error(error);
    }
  };

  // AI Prompt Modal - use ref to avoid cursor jumping issues
  const aiPromptRef = useRef(null);
  
  // Sync ref value to state only when needed (before generation)
  const getAiPromptValue = useCallback(() => {
    return aiPromptRef.current?.value || '';
  }, []);
  
  // Reset the ref when dialog opens
  useEffect(() => {
    if (showAIPrompt && aiPromptRef.current) {
      aiPromptRef.current.value = aiCustomPrompt;
      // Focus the textarea with a small delay to ensure dialog is rendered
      setTimeout(() => {
        aiPromptRef.current?.focus();
      }, 100);
    }
  }, [showAIPrompt]);

  // Media Section Component
  const MediaSection = ({ form, formSetter, type }) => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">Media Gallery</label>
        <div className="flex gap-2">
          <label className="cursor-pointer">
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => handleMediaUpload(e, type, formSetter)}
            />
            <Button type="button" variant="outline" size="sm" disabled={uploading} asChild>
              <span><Image className="h-4 w-4 mr-1" /> Add Images</span>
            </Button>
          </label>
        </div>
      </div>
      
      {/* Media Grid */}
      {form.media && form.media.length > 0 && (
        <div className="grid grid-cols-4 gap-3">
          {form.media.map((item, idx) => (
            <div key={idx} className="relative group aspect-square bg-gray-100 rounded-lg overflow-hidden">
              {item.type === 'video' ? (
                <div className="w-full h-full flex items-center justify-center bg-gray-800">
                  <Video className="h-8 w-8 text-white" />
                </div>
              ) : (
                <img src={item.url} alt="" className="w-full h-full object-cover" />
              )}
              <button
                type="button"
                onClick={() => removeMedia(idx, formSetter)}
                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {uploading && (
        <div className="text-center py-4 text-gray-500">
          <div className="animate-spin h-6 w-6 border-2 border-purple-600 border-t-transparent rounded-full mx-auto mb-2"></div>
          Uploading...
        </div>
      )}
    </div>
  );

  // Product Selector Component
  const ProductSelector = ({ value, onChange }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        <Package className="h-4 w-4 inline mr-1" />
        Featured Product (shown in content)
      </label>
      <select 
        value={value || ''} 
        onChange={(e) => onChange(e.target.value)} 
        className="w-full p-3 border rounded-lg"
      >
        <option value="">No featured product</option>
        {products.map(p => (
          <option key={p.id} value={p.id}>{p.name} - ${p.price}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="animate-fade-in" data-testid="content-manager">
      <h1 className="text-3xl font-bold text-[#0a0203] mb-6">Content Management</h1>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6 bg-gray-100 p-1 rounded-lg">
          <TabsTrigger value="blogs" className="px-6 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <FileText className="h-4 w-4 mr-2" /> Blog Posts ({blogs.length})
          </TabsTrigger>
          <TabsTrigger value="categories" className="px-6 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="categories-tab">
            <Tag className="h-4 w-4 mr-2" /> Categories ({categories.length})
          </TabsTrigger>
          <TabsTrigger value="recipes" className="px-6 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <ChefHat className="h-4 w-4 mr-2" /> Recipes ({recipes.length})
          </TabsTrigger>
        </TabsList>

        {/* Blogs Tab */}
        <TabsContent value="blogs">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setShowBlogDialog(true)} className="bg-[#003456]" data-testid="new-blog-btn">
              <Plus className="h-4 w-4 mr-2" /> New Blog Post
            </Button>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Post</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Category</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Views</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {blogs.map(post => (
                  <tr key={post.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        {post.featured_image && (
                          <img src={post.featured_image} alt="" className="w-16 h-12 rounded-lg object-cover" />
                        )}
                        <div>
                          <p className="font-medium text-[#0a0203]">{post.title}</p>
                          <p className="text-sm text-gray-500">/{post.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={post.category_id || ''}
                        onChange={(e) => handleQuickCategoryChange(post.id, e.target.value)}
                        className="w-full max-w-[180px] px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white hover:border-[#003456] focus:border-[#003456] focus:ring-1 focus:ring-[#003456] cursor-pointer transition-colors"
                        data-testid={`quick-category-${post.id}`}
                      >
                        <option value="">No Category</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${post.published ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}>
                          {post.published ? 'Published' : 'Draft'}
                        </span>
                        {post.featured && (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-600">Featured</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{post.views || 0}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {post.published && (
                          <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
                            <Eye className="h-4 w-4" />
                          </a>
                        )}
                        <button onClick={() => handleEditBlog(post)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDeleteBlog(post.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {blogs.length === 0 && (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No blog posts yet</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setShowCategoryDialog(true)} className="bg-[#003456]" data-testid="new-category-btn">
              <Plus className="h-4 w-4 mr-2" /> New Category
            </Button>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Category Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Slug</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Description</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Posts</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map(cat => {
                  const postsCount = blogs.filter(b => b.category_id === cat.id).length;
                  return (
                    <tr key={cat.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`category-row-${cat.id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Tag className="h-4 w-4 text-[#003456]" />
                          <span className="font-medium text-[#0a0203]">{cat.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600">/{cat.slug}</td>
                      <td className="px-6 py-4 text-gray-600 max-w-xs truncate">{cat.description || '-'}</td>
                      <td className="px-6 py-4">
                        <button 
                          onClick={() => handleOpenBulkAssign(cat)}
                          className="inline-flex items-center gap-1 text-[#003456] hover:underline font-medium"
                          data-testid={`assign-posts-${cat.id}`}
                        >
                          <FileText className="h-3.5 w-3.5" />
                          {postsCount} post{postsCount !== 1 ? 's' : ''}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button 
                            onClick={() => handleOpenBulkAssign(cat)} 
                            className="p-2 text-[#003456] hover:bg-blue-50 rounded-lg" 
                            title="Assign Posts"
                            data-testid={`bulk-assign-${cat.id}`}
                          >
                            <ListChecks className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleEditCategory(cat)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg" data-testid={`edit-category-${cat.id}`}>
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleDeleteCategory(cat.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" data-testid={`delete-category-${cat.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {categories.length === 0 && (
              <div className="text-center py-12">
                <Tag className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No categories yet. Create one to organize your blog posts.</p>
              </div>
            )}
          </div>
          
          {/* Suggested Categories */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-700 mb-2">Suggested Categories:</h4>
            <p className="text-sm text-gray-600">Technical, Maintenance, Lifestyle, Tips & Tricks, Product Updates, How-To Guides</p>
          </div>
        </TabsContent>

        {/* Recipes Tab */}
        <TabsContent value="recipes">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setShowRecipeDialog(true)} className="bg-[#003456]" data-testid="new-recipe-btn">
              <Plus className="h-4 w-4 mr-2" /> New Recipe
            </Button>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Recipe</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Difficulty</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Views</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {recipes.map(recipe => (
                  <tr key={recipe.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        {recipe.featured_image && (
                          <img src={recipe.featured_image} alt="" className="w-16 h-12 rounded-lg object-cover" />
                        )}
                        <div>
                          <p className="font-medium text-[#0a0203]">{recipe.title}</p>
                          <p className="text-sm text-gray-500">/{recipe.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600 capitalize">{recipe.difficulty || '-'}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${recipe.published ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}>
                          {recipe.published ? 'Published' : 'Draft'}
                        </span>
                        {recipe.featured && (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-600">Featured</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{recipe.views || 0}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {recipe.published && (
                          <a href={`/recipes/${recipe.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
                            <Eye className="h-4 w-4" />
                          </a>
                        )}
                        <button onClick={() => handleEditRecipe(recipe)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDeleteRecipe(recipe.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {recipes.length === 0 && (
              <div className="text-center py-12">
                <ChefHat className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No recipes yet</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Blog Dialog */}
      <Dialog open={showBlogDialog} onOpenChange={(open) => { if (!open) resetBlogForm(); else setShowBlogDialog(open); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBlogId ? 'Edit Blog Post' : 'Create Blog Post'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveBlog} className="space-y-4 pt-4">
            {/* AI Generator - Enhanced */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                  <span className="font-medium text-purple-800">AI Content Generator</span>
                </div>
                <div className="flex gap-2">
                  <Button type="button" onClick={() => setShowAIPrompt(true)} variant="outline" className="border-purple-300 text-purple-700" size="sm">
                    Custom Prompt
                  </Button>
                  <Button 
                    type="button" 
                    onClick={() => { setAiCustomPrompt(''); generateBlogContent(); }} 
                    disabled={aiGenerating || !blogForm.title} 
                    className="bg-purple-600 hover:bg-purple-700" 
                    size="sm"
                    data-testid="quick-generate-btn"
                  >
                    {aiGenerating ? (
                      <><span className="animate-spin mr-2">⟳</span> Generating...</>
                    ) : 'Quick Generate'}
                  </Button>
                </div>
              </div>
              
              {/* AI Options */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <label className="text-xs text-purple-700 block mb-1">Tone</label>
                  <select 
                    value={aiOptions.tone} 
                    onChange={(e) => setAiOptions({...aiOptions, tone: e.target.value})}
                    className="w-full text-xs p-1.5 border rounded bg-white"
                  >
                    <option value="informative">Informative</option>
                    <option value="conversational">Conversational</option>
                    <option value="expert">Expert/Technical</option>
                    <option value="friendly">Friendly</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-purple-700 block mb-1">Word Count</label>
                  <select 
                    value={aiOptions.wordCount} 
                    onChange={(e) => setAiOptions({...aiOptions, wordCount: parseInt(e.target.value)})}
                    className="w-full text-xs p-1.5 border rounded bg-white"
                  >
                    <option value="500">~500 (Short)</option>
                    <option value="800">~800 (Medium)</option>
                    <option value="1200">~1200 (Long)</option>
                    <option value="2000">~2000 (In-depth)</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-purple-700 block mb-1">Auto-fill Fields</label>
                  <div className="flex flex-wrap gap-2">
                    <label className="flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={aiOptions.generateSlug} onChange={(e) => setAiOptions({...aiOptions, generateSlug: e.target.checked})} className="rounded" />
                      Slug
                    </label>
                    <label className="flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={aiOptions.generateExcerpt} onChange={(e) => setAiOptions({...aiOptions, generateExcerpt: e.target.checked})} className="rounded" />
                      Excerpt
                    </label>
                    <label className="flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={aiOptions.generateTags} onChange={(e) => setAiOptions({...aiOptions, generateTags: e.target.checked})} className="rounded" />
                      Tags
                    </label>
                    <label className="flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={aiOptions.generateMeta} onChange={(e) => setAiOptions({...aiOptions, generateMeta: e.target.checked})} className="rounded" />
                      SEO Meta
                    </label>
                  </div>
                </div>
              </div>
              
              {!blogForm.title && (
                <p className="text-xs text-purple-600 mt-2">💡 Enter a blog title above to enable AI generation</p>
              )}
            </div>

            {/* Basic Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <Input placeholder="Blog Title *" value={blogForm.title} onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })} required />
              <Input placeholder="URL Slug *" value={blogForm.slug} onChange={(e) => setBlogForm({ ...blogForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} required />
            </div>

            {/* Featured Image - Upload + URL */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                <Image className="h-4 w-4 inline mr-1" />
                Featured Image
              </label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input 
                    placeholder="Image URL" 
                    value={blogForm.featured_image} 
                    onChange={(e) => setBlogForm({ ...blogForm, featured_image: e.target.value })} 
                  />
                </div>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFeaturedImageUpload(e, 'blog')}
                    className="hidden"
                    id="blog-featured-image-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={uploadingFeaturedImage}
                    onClick={() => document.getElementById('blog-featured-image-upload').click()}
                  >
                    {uploadingFeaturedImage ? 'Uploading...' : <><Upload className="h-4 w-4 mr-1" /> Upload</>}
                  </Button>
                </div>
              </div>
              {blogForm.featured_image && (
                <div className="mt-2 relative inline-block">
                  <img src={blogForm.featured_image} alt="Featured" className="h-24 rounded-lg object-cover" />
                  <button
                    type="button"
                    onClick={() => setBlogForm({ ...blogForm, featured_image: '' })}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>

            <Input placeholder="Video URL (YouTube/Vimeo)" value={blogForm.video_url || ''} onChange={(e) => setBlogForm({ ...blogForm, video_url: e.target.value })} />

            {/* Media Gallery */}
            <MediaSection form={blogForm} formSetter={setBlogForm} type="blog" />

            {/* Featured Product */}
            <ProductSelector value={blogForm.featured_product_id} onChange={(val) => setBlogForm({ ...blogForm, featured_product_id: val })} />

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
              <RichTextEditor
                value={blogForm.content}
                onChange={(value) => setBlogForm({ ...blogForm, content: value })}
                placeholder="Write your blog content here..."
              />
            </div>

            <Input placeholder="Excerpt (short summary)" value={blogForm.excerpt} onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })} />

            <div className="grid md:grid-cols-2 gap-4">
              <select value={blogForm.category_id} onChange={(e) => setBlogForm({ ...blogForm, category_id: e.target.value })} className="w-full p-3 border rounded-lg">
                <option value="">Select Category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              <Input placeholder="Tags (comma-separated)" value={blogForm.tags} onChange={(e) => setBlogForm({ ...blogForm, tags: e.target.value })} />
            </div>

            {/* Embedded Marketing - Banners & Coupons */}
            <div className="border-t pt-4 space-y-4">
              <h4 className="font-medium text-gray-700 flex items-center gap-2">
                <Megaphone className="h-4 w-4" /> Embedded Marketing
              </h4>
              
              {/* Banners Selection */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-gray-700">Campaign Banners ({banners.length})</span>
                </div>
                <p className="text-xs text-gray-500 mb-3">Select banners to display in this blog post</p>
                {banners.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {banners.filter(b => b.is_active !== false).map(banner => (
                      <div key={banner.id} className="flex items-center justify-between bg-white p-2 rounded border">
                        <label className="flex items-center gap-2 cursor-pointer flex-1">
                          <input
                            type="checkbox"
                            checked={blogForm.embedded_banner_ids.includes(banner.id)}
                            onChange={() => toggleBannerInBlog(banner.id)}
                            className="w-4 h-4 rounded"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-medium">{banner.name || banner.title}</span>
                            <p className="text-xs text-gray-500 truncate">{banner.text}</p>
                          </div>
                          <span 
                            className="w-4 h-4 rounded-full flex-shrink-0" 
                            style={{ backgroundColor: banner.background_color || '#003456' }}
                            title={banner.location}
                          />
                        </label>
                        {blogForm.embedded_banner_ids.includes(banner.id) && (
                          <select
                            value={blogForm.banner_positions[banner.id] || 'middle'}
                            onChange={(e) => updateBannerPosition(banner.id, e.target.value)}
                            className="text-xs p-1 border rounded ml-2"
                          >
                            <option value="top">Top of Post</option>
                            <option value="middle">Middle of Post</option>
                            <option value="bottom">Bottom of Post</option>
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">No banners available. Create banners in Marketing Dashboard.</p>
                )}
              </div>

              {/* Coupons Selection */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Percent className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-gray-700">Coupons & Discounts ({coupons.length})</span>
                </div>
                <p className="text-xs text-gray-500 mb-3">Select coupons to display in this blog post</p>
                {coupons.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {coupons.filter(c => c.active !== false).map(coupon => (
                      <div key={coupon.id} className="flex items-center justify-between bg-white p-2 rounded border">
                        <label className="flex items-center gap-2 cursor-pointer flex-1">
                          <input
                            type="checkbox"
                            checked={blogForm.embedded_coupon_ids.includes(coupon.id)}
                            onChange={() => toggleCouponInBlog(coupon.id)}
                            className="w-4 h-4 rounded"
                          />
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-mono bg-green-100 text-green-700 px-2 py-0.5 rounded">{coupon.code}</span>
                            <span className="text-xs font-medium text-green-600">
                              {coupon.discount_type === 'percentage' ? `${coupon.discount_value}% OFF` : `$${coupon.discount_value} OFF`}
                            </span>
                          </div>
                          {coupon.description && (
                            <span className="text-xs text-gray-500 truncate ml-2">{coupon.description}</span>
                          )}
                        </label>
                        {blogForm.embedded_coupon_ids.includes(coupon.id) && (
                          <select
                            value={blogForm.coupon_positions[coupon.id] || 'bottom'}
                            onChange={(e) => updateCouponPosition(coupon.id, e.target.value)}
                            className="text-xs p-1 border rounded ml-2"
                          >
                            <option value="top">Top of Post</option>
                            <option value="middle">Middle of Post</option>
                            <option value="bottom">Bottom of Post</option>
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">No coupons available. Create coupons in Marketing Dashboard.</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={blogForm.published} onChange={(e) => setBlogForm({ ...blogForm, published: e.target.checked })} className="w-4 h-4" />
                <span>Published</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={blogForm.featured} onChange={(e) => setBlogForm({ ...blogForm, featured: e.target.checked })} className="w-4 h-4" />
                <span>Featured</span>
              </label>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={resetBlogForm}>Cancel</Button>
              <Button type="submit" className="bg-[#003456]">{editingBlogId ? 'Update' : 'Create'} Blog Post</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Recipe Dialog */}
      <Dialog open={showRecipeDialog} onOpenChange={(open) => { if (!open) resetRecipeForm(); else setShowRecipeDialog(open); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRecipeId ? 'Edit Recipe' : 'Create Recipe'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveRecipe} className="space-y-4 pt-4">
            {/* AI Recipe Generator - Enhanced */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                  <span className="font-medium text-purple-800">AI Recipe Generator</span>
                </div>
                <div className="flex gap-2">
                  <Button type="button" onClick={() => setShowAIPrompt(true)} variant="outline" className="border-purple-300 text-purple-700" size="sm">
                    Custom Prompt
                  </Button>
                  <Button 
                    type="button" 
                    onClick={() => { setAiCustomPrompt(''); generateRecipeContent(); }} 
                    disabled={aiGenerating || !recipeForm.title} 
                    className="bg-purple-600 hover:bg-purple-700" 
                    size="sm"
                    data-testid="recipe-quick-generate-btn"
                  >
                    {aiGenerating ? (
                      <><span className="animate-spin mr-2">⟳</span> Generating...</>
                    ) : 'Quick Generate'}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-purple-700">
                AI will generate: Description, Ingredients, Instructions, Prep/Cook times, Servings, Difficulty, Tags, and URL Slug
              </p>
              {!recipeForm.title && (
                <p className="text-xs text-purple-600 mt-2">💡 Enter a recipe name above to enable AI generation</p>
              )}
            </div>

            {/* Basic Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <Input placeholder="Recipe Title *" value={recipeForm.title} onChange={(e) => setRecipeForm({ ...recipeForm, title: e.target.value })} required />
              <Input placeholder="URL Slug *" value={recipeForm.slug} onChange={(e) => setRecipeForm({ ...recipeForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} required />
            </div>

            <textarea placeholder="Description" value={recipeForm.description} onChange={(e) => setRecipeForm({ ...recipeForm, description: e.target.value })} className="w-full min-h-[80px] p-3 border rounded-lg resize-y" />

            {/* Featured Image - Upload + URL */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                <Image className="h-4 w-4 inline mr-1" />
                Featured Image
              </label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input 
                    placeholder="Image URL" 
                    value={recipeForm.featured_image} 
                    onChange={(e) => setRecipeForm({ ...recipeForm, featured_image: e.target.value })} 
                  />
                </div>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFeaturedImageUpload(e, 'recipe')}
                    className="hidden"
                    id="recipe-featured-image-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={uploadingFeaturedImage}
                    onClick={() => document.getElementById('recipe-featured-image-upload').click()}
                  >
                    {uploadingFeaturedImage ? 'Uploading...' : <><Upload className="h-4 w-4 mr-1" /> Upload</>}
                  </Button>
                </div>
              </div>
              {recipeForm.featured_image && (
                <div className="mt-2 relative inline-block">
                  <img src={recipeForm.featured_image} alt="Featured" className="h-24 rounded-lg object-cover" />
                  <button
                    type="button"
                    onClick={() => setRecipeForm({ ...recipeForm, featured_image: '' })}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>

            <Input placeholder="Video URL (YouTube/Vimeo)" value={recipeForm.video_url || ''} onChange={(e) => setRecipeForm({ ...recipeForm, video_url: e.target.value })} />

            {/* Media Gallery */}
            <MediaSection form={recipeForm} formSetter={setRecipeForm} type="recipe" />

            {/* Featured Product */}
            <ProductSelector value={recipeForm.featured_product_id} onChange={(val) => setRecipeForm({ ...recipeForm, featured_product_id: val })} />

            {/* Time & Servings */}
            <div className="grid grid-cols-4 gap-4">
              <Input type="number" placeholder="Prep (min)" value={recipeForm.prep_time} onChange={(e) => setRecipeForm({ ...recipeForm, prep_time: e.target.value })} />
              <Input type="number" placeholder="Rehydrate" value={recipeForm.rehydration_time} onChange={(e) => setRecipeForm({ ...recipeForm, rehydration_time: e.target.value })} />
              <Input type="number" placeholder="Cook (min)" value={recipeForm.cook_time} onChange={(e) => setRecipeForm({ ...recipeForm, cook_time: e.target.value })} />
              <Input type="number" placeholder="Servings" value={recipeForm.servings} onChange={(e) => setRecipeForm({ ...recipeForm, servings: e.target.value })} />
            </div>

            <select value={recipeForm.difficulty} onChange={(e) => setRecipeForm({ ...recipeForm, difficulty: e.target.value })} className="w-full p-3 border rounded-lg">
              <option value="">Select Difficulty</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>

            {/* Ingredients */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ingredients</label>
              <RichTextEditor
                value={recipeForm.ingredients}
                onChange={(value) => setRecipeForm({ ...recipeForm, ingredients: value })}
                placeholder="Add ingredients (use bullet list)..."
              />
            </div>

            {/* Instructions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Instructions</label>
              <RichTextEditor
                value={recipeForm.instructions}
                onChange={(value) => setRecipeForm({ ...recipeForm, instructions: value })}
                placeholder="Add step-by-step instructions..."
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Input placeholder="Categories (comma-separated)" value={recipeForm.categories} onChange={(e) => setRecipeForm({ ...recipeForm, categories: e.target.value })} />
              <Input placeholder="Tags (comma-separated)" value={recipeForm.tags} onChange={(e) => setRecipeForm({ ...recipeForm, tags: e.target.value })} />
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={recipeForm.published} onChange={(e) => setRecipeForm({ ...recipeForm, published: e.target.checked })} className="w-4 h-4" />
                <span>Published</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={recipeForm.featured} onChange={(e) => setRecipeForm({ ...recipeForm, featured: e.target.checked })} className="w-4 h-4" />
                <span>Featured</span>
              </label>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={resetRecipeForm}>Cancel</Button>
              <Button type="submit" className="bg-[#003456]">{editingRecipeId ? 'Update' : 'Create'} Recipe</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={(open) => { if (!open) resetCategoryForm(); else setShowCategoryDialog(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCategoryId ? 'Edit Category' : 'New Category'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveCategory} className="space-y-4 pt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category Name *</label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Technical, Maintenance, Lifestyle"
                required
                data-testid="category-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug (URL-friendly name)</label>
              <Input
                value={categoryForm.slug}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, slug: e.target.value }))}
                placeholder="auto-generated from name if left empty"
                data-testid="category-slug-input"
              />
              <p className="text-xs text-gray-500 mt-1">Used in URLs: /blog?category=slug</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                className="w-full p-3 border rounded-lg h-20 resize-none"
                placeholder="Brief description of what this category covers"
                data-testid="category-description-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={resetCategoryForm}>Cancel</Button>
              <Button type="submit" className="bg-[#003456]" data-testid="save-category-btn">
                {editingCategoryId ? 'Update' : 'Create'} Category
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Posts to Category Dialog */}
      <Dialog open={showBulkAssignDialog} onOpenChange={(open) => { if (!open) resetBulkAssignDialog(); else setShowBulkAssignDialog(open); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListChecks className="h-5 w-5 text-[#003456]" />
              Assign Posts to "{bulkAssignCategoryName}"
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden flex flex-col pt-4">
            <div className="flex items-center justify-between mb-4 pb-3 border-b">
              <p className="text-sm text-gray-600">
                Select the blog posts you want to assign to this category. 
                <span className="font-medium text-[#003456]"> {selectedPostIds.length} selected</span>
              </p>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={handleSelectAllPosts}
                data-testid="select-all-posts-btn"
              >
                {selectedPostIds.length === blogs.length ? (
                  <><Square className="h-4 w-4 mr-1" /> Deselect All</>
                ) : (
                  <><CheckSquare className="h-4 w-4 mr-1" /> Select All</>
                )}
              </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto space-y-2 pr-2">
              {blogs.length > 0 ? blogs.map(post => (
                <label 
                  key={post.id}
                  className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedPostIds.includes(post.id) 
                      ? 'bg-blue-50 border border-blue-200' 
                      : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                  }`}
                  data-testid={`bulk-assign-post-${post.id}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedPostIds.includes(post.id)}
                    onChange={() => handleTogglePostSelection(post.id)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-[#003456]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[#0a0203] truncate">{post.title}</span>
                      {post.published ? (
                        <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">Published</span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">Draft</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate mt-0.5">
                      {buildPreview(post, 100) || 'No excerpt'}
                    </p>
                    {post.category_id && post.category_id !== bulkAssignCategoryId && (
                      <p className="text-xs text-orange-600 mt-1">
                        Currently in: {categories.find(c => c.id === post.category_id)?.name || 'Unknown'}
                      </p>
                    )}
                  </div>
                  {selectedPostIds.includes(post.id) && (
                    <Check className="h-5 w-5 text-[#003456] flex-shrink-0" />
                  )}
                </label>
              )) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No blog posts available</p>
                </div>
              )}
            </div>
            
            <div className="flex justify-between items-center pt-4 mt-4 border-t">
              <p className="text-sm text-gray-500">
                {selectedPostIds.length} of {blogs.length} posts selected
              </p>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={resetBulkAssignDialog}>Cancel</Button>
                <Button 
                  onClick={handleSaveBulkAssign}
                  className="bg-[#003456]" 
                  disabled={savingBulkAssign}
                  data-testid="save-bulk-assign-btn"
                >
                  {savingBulkAssign ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Prompt Modal - Using uncontrolled textarea to prevent cursor jumping */}
      <Dialog open={showAIPrompt} onOpenChange={(open) => {
        if (!open) {
          // Save the current value when closing
          if (aiPromptRef.current) {
            setAiCustomPrompt(aiPromptRef.current.value);
          }
        }
        setShowAIPrompt(open);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              AI Content Generator
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-sm text-purple-700">
                <strong>Tips for better results:</strong>
              </p>
              <ul className="text-xs text-purple-600 mt-1 list-disc pl-4">
                <li>Be specific about your target audience</li>
                <li>Mention the tone you want (casual, professional, friendly)</li>
                <li>Include specific features or benefits to highlight</li>
              </ul>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Custom Prompt (optional)</label>
              <textarea
                ref={aiPromptRef}
                defaultValue={aiCustomPrompt}
                placeholder={`Example: Write about freeze drying ${activeTab === 'blogs' ? 'tips for beginners' : 'fruits for hiking snacks'}. Focus on practical advice and include pro tips.`}
                className="w-full mt-1 p-3 border rounded-lg h-24 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="ai-custom-prompt-textarea"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAIPrompt(false)}>Cancel</Button>
              <Button 
                onClick={activeTab === 'blogs' ? generateBlogContent : generateRecipeContent}
                disabled={aiGenerating}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="ai-generate-content-btn"
              >
                {aiGenerating ? 'Generating...' : 'Generate Content'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentManager;
